import { TranslateService } from '@ngx-translate/core';
import {
  Component,
  ElementRef,
  AfterViewInit,
  OnChanges,
  Input,
  HostListener,
} from '@angular/core';
import { ChartComponent } from '../chart.component';
import { select, selectAll, mouse } from 'd3-selection';
import { axisBottom, axisLeft } from 'd3-axis';
import { scaleLinear, scaleOrdinal, scaleBand } from 'd3-scale';
import "d3-transition/index";

@Component({
  selector: 'app-stacked-bar-chart',
  template: '',
  styleUrls: ['./stacked-bar-chart.component.scss'],
})
export class StackedBarChartComponent
  extends ChartComponent
  implements AfterViewInit, OnChanges {
  @Input() allGroup: string[];
  @Input() data: any[];
  @Input() useGrid: boolean = true;
  @Input() unit: string = '';
  @Input() legendTranslationKey: string;
  @Input() showLegend: boolean = false;
  @Input() minThreshold: number;
  @Input() maxThreshold: number;
  myColor: any;
  xAxis: any;
  yAxis: any;
  chartWithLegend: any;
  lastWidth: any;
  subGroups: string[];
  groups: string[];

  rows;

  colors = [
    '#cc1600',
    '#003761',
    '#9185be',
    '#607d8b',
    '#8ec89a',
    '#a4917e',
    '#666666',
    '#5a1414',
    '#cccccc',
    '#f3997b',
    '#cfe09b',
    '#488ccb',
  ];

  tooltip: any;
  mouseover: (d: any) => void;
  mousemove: (d: any) => void;
  mouseleave: (d: any) => void;
  xSubgroup: any;

  @HostListener('window:mouseup')
  onMouseUp() {
    if (this.el.nativeElement.offsetWidth !== this.lastWidth) {
      this.lastWidth = this.el.nativeElement.offsetWidth;
      setTimeout(this.redrawChart.bind(this), 500);
    }
  }

  constructor(protected el: ElementRef, private translate: TranslateService) {
    super(el);
  }

  ngAfterViewInit() {
    this.subGroups = this.data.slice(0)[0].filter((key) => key !== 'group');
    this.rows = this.data.slice(1, this.data.length);
    this.groups = [...this.rows.map((row) => row.group)];
    this.drawChart();
  }

  ngOnChanges(): void {
    if (!this.data) {
      return;
    }
    this.subGroups = this.data.slice(0)[0].filter((key) => key !== 'group');
    this.rows = this.data.slice(1, this.data.length);
    this.groups = [...this.rows.map((row) => row.group)];
    this.removeChart();
    this.drawChart();
  }

  drawChart() {
    this.updateChartDimentions();
    if (this.width && this.height && this.rows) {
      this.updateDimensions();
      this.initElements();
      this.prepareColors();
      this.addXYAxis();
      this.drawLines();
      this.drawLegend();
      this.drawTooltip();
    }
  }
  drawTooltip() {
    const self = this;
    this.tooltip = this.chartWithLegend
      .append('div')
      .style('opacity', 0)
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('background-color', 'white')
      .style('border', 'solid')
      .style('border-width', '1px')
      .style('border-radius', '5px')
      .style('padding', '10px');

    // Three function that change the tooltip when user hover / move / leave a cell
    this.mouseover = function (d) {
      const subgroupName = d.key;
      const subgroupValue = d.value;
      self.tooltip
        .html(
          self.translate.instant(self.legendTranslationKey + subgroupName) +
            '<br>' +
            self.translate.instant(self.legendTranslationKey + 'value', {
              value: subgroupValue,
            })
        )
        .style('opacity', 1)
        .style('pointer-events', 'auto');
    };
    this.mousemove = function (d) {
      self.tooltip
        .style(
          'left',
          self.xAxis((select(this.parentNode).datum() as any).group) +
            mouse(this)[0] +
            90 +
            'px'
        )
        .style('top', mouse(this)[1] + 'px');
    };
    this.mouseleave = function (d) {
      self.tooltip.style('opacity', 0).style('pointer-events', 'none');
    };
  }
  // .color {
  //     height: 12px;
  //     width: 12px;
  //     margin-right: 5px;
  // }
  private drawLegend() {
    const legendDiv = this.chartWithLegend
      .append('div')
      .style('display', 'flex')
      .style('justify-content', 'center')
      .selectAll('myLegend')
      .data(this.subGroups)
      .enter()
      .append('div')
      .attr('class', 'color')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('padding', '10px');
    legendDiv
      .append('div')
      .style('height', '12px')
      .style('width', '12px')
      .style('margin-right', '5px')
      .style('background-color', (d) => {
        return this.myColor(d);
      });
    legendDiv
      .append('text')
      .style('cursor', 'pointer')
      .attr('x', function (d, i) {
        return 30 + i * 60;
      })
      .attr('y', 30)
      .text((d) => {
        return this.translate.instant(this.legendTranslationKey + d);
      })
      .style('font-size', 15)
      .on('click', function (d) {
        const currentOpacity = selectAll('.' + d).style('opacity');
        select(this).style(
          'text-decoration',
          currentOpacity === '1' ? 'line-through' : 'none'
        );
        selectAll('.' + d)
          .transition()
          .style('opacity', currentOpacity === '1' ? '0' : '1')
          .style('pointer-events', currentOpacity === '1' ? 'none' : 'auto');
      });
  }

  updateChartDimentions() {
    this.chartElement = this.el.nativeElement;
    this.width = this.chartElement.offsetWidth;
    this.height = this.chartElement.offsetHeight - 50;
  }

  private drawLines() {
    const thisScope = this;
    this.xSubgroup = scaleBand()
      .domain(this.subGroups)
      .range([0, this.xAxis.bandwidth()])
      .padding(0.05);
    // Add the lines
    this.svg
      .append('g')
      .selectAll('g')
      // Enter in data = loop group per group
      .data(this.rows)
      .enter()
      .append('g')
      .attr('transform', (d: any) => 'translate(' + this.xAxis(d.group) + ',0)')
      .selectAll('rect')
      .data((d) =>
        this.subGroups.map(function (key) {
          return { key: key, value: d[key] };
        })
      )
      .enter()
      .append('rect')
      .attr('class', (d) => {
        return d.key;
      })
      .attr('x', (d) => this.xSubgroup(d.key))
      .attr('y', (d) => this.yAxis(d.value))
      .attr('width', this.xSubgroup.bandwidth())
      .attr('height', (d) => this.height - this.yAxis(d.value))
      .attr('fill', (d) => this.myColor(d.key))
      .on('mouseover', thisScope.mouseover)
      .on('mousemove', thisScope.mousemove)
      .on('mouseleave', thisScope.mouseleave);
  }

  private addXYAxis() {
    this.xAxis = scaleBand()
      .domain(this.groups)
      .range([0, this.width])
      .padding(0.2);
    this.svg
      .append('g')
      .attr('class', 'grid')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(
        axisBottom(this.xAxis)
          .tickPadding(20)
          .tickSizeInner(-this.height)
          .tickSizeOuter(10)
      )
      .selectAll('text')
      .attr('transform', 'rotate(15)')
      .style('text-anchor', 'middle');
    this.svg
      .selectAll('.tick > line')
      .style('stroke', 'lightgrey')
      .style('stroke-opacity', 0.7);

    this.yAxis = scaleLinear()
      .domain([
        0,
        Math.max(
          Math.max(
            ...this.rows
              .map((group) =>
                Math.max(...this.subGroups.map((subGroup) => group[subGroup]))
              )
              .filter((val) => !isNaN(val))
          )
        ),
      ])
      .nice()
      .range([this.height, 0]);
    this.svg
      .append('g')
      .attr('class', 'grid')
      .call(
        axisLeft(this.yAxis)
          .tickPadding(15)
          .tickSizeInner(-this.width)
          .tickSizeOuter(10)
      )
      .selectAll('.tick > line')
      .style('stroke', 'lightgrey')
      .style('stroke-opacity', 0.7);
  }

  private prepareColors() {
    this.myColor = scaleOrdinal()
      .domain(this.subGroups)
      .range(this.colors)
      .bind(this);
  }

  private updateDimensions() {
    this.height = this.height - this.margin.top - this.margin.bottom;
    this.width = this.width - this.margin.left - this.margin.right;
  }

  private initElements() {
    this.chartWithLegend = select(this.chartElement).append('div');
    this.svg = this.chartWithLegend
      .append('svg')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom + 20)
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
  }
}
